import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import {
  Users, Shield, KeyRound, ScrollText, UserPlus, Plus, Search,
  Eye, Lock, CheckCircle2, XCircle,
  LoaderCircle, Download, RefreshCw, Save,   ChevronDown, X, UserCog,
  UserCheck, UserX, Activity, AlertTriangle, ShieldCheck,
  LayoutDashboard, Briefcase, BarChart3, Settings, Gauge, PiggyBank,
  HandCoins, Receipt, Bot, Key, Info,
  ChevronLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getDefaultAvatar } from "@/utils/getDefaultAvatar";
import useConfirmModal from "@/hooks/useConfirmModal";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import ConfirmModal from "@/components/modal/ConfirmModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import DataTableRowActions from "@/components/datatable/DataTableRowActions";
import PermissionsMatrix from "@/components/permissions/PermissionsMatrix";

const MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "members", label: "Member Management" },
  { key: "contributions", label: "Savings & Contributions" },
  { key: "loans", label: "Loans & Payments" },
  { key: "expenses", label: "Expenses & Bills" },
  { key: "meetings", label: "Meetings & Attendance" },
  { key: "reports", label: "Reports & Analytics" },
  { key: "payroll", label: "HR Payroll" },
  { key: "ai", label: "AI Assistant" },
  { key: "settings", label: "System Settings" },
  { key: "users", label: "Users & Roles" },
  { key: "portal", label: "Member Portal" },
] as const;

const ACTIONS = ["view", "create", "edit", "delete", "approve", "reject", "verify", "export", "print", "import", "manage"] as const;

const MODULE_ACTIONS: Record<string, string[]> = {
  dashboard: ["view"],
  members: ["view", "create", "edit", "delete", "approve", "export", "import"],
  contributions: ["view", "create", "edit", "delete", "verify", "export", "import"],
  loans: ["view", "create", "edit", "delete", "approve", "reject", "verify", "export"],
  expenses: ["view", "create", "edit", "delete", "approve", "export"],
  meetings: ["view", "create", "edit", "delete", "export"],
  reports: ["view", "export", "print"],
  payroll: ["view", "create", "edit", "delete", "export", "import"],
  ai: ["view"],
  settings: ["view", "edit", "manage"],
  users: ["view", "create", "edit", "delete", "manage"],
  portal: ["view"],
};

function formatDate(d: string | Date | null | undefined) {
  if (!d) return "N/A";
  try { return format(new Date(d), "MMM dd, yyyy"); } catch { return "N/A"; }
}

function formatDateTime(d: string | Date | null | undefined) {
  if (!d) return "N/A";
  try { return format(new Date(d), "MMM dd, yyyy HH:mm"); } catch { return "N/A"; }
}

const userSchema = z.object({
  first_name: z.string().min(2, "First name required"),
  last_name: z.string().min(2, "Last name required"),
  email: z.string().email("Invalid email"),
  phone: z.string().optional().or(z.literal("")),
  password: z.union([z.string().min(6, "Min 6 characters"), z.literal(""), z.undefined()]).optional(),
  roleId: z.union([z.number(), z.string(), z.null(), z.undefined()]).optional(),
  role: z.string().optional(),
  status: z.enum(["active", "inactive"]),
  requiresPasswordChange: z.boolean().optional(),
  memberId: z.union([z.number(), z.null()]).optional(),
});

const roleSchema = z.object({
  name: z.string().min(2, "Role name required"),
  code: z.string().optional().or(z.literal("")),
  description: z.string().optional().or(z.literal("")),
  status: z.enum(["active", "inactive"]).optional(),
});

// ─── Role Form Modal (redesigned) ────────────────────────────────────
function RoleForm({ open, onClose, role, onSuccess }: { open: boolean; onClose: () => void; role?: any; onSuccess: () => void }) {
  const [saving, setSaving] = useState(false);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const isEdit = !!role;

  const usedForm = useForm<z.infer<typeof roleSchema>>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: role?.name || "",
      code: role?.code || "",
      description: role?.description || "",
      status: role?.status || "active",
    },
  });

  const formName = usedForm.watch("name");

  const hasUnsaved = usedForm.formState.isDirty;

  useEffect(() => {
    if (open) {
      usedForm.reset({
        name: role?.name || "",
        code: role?.code || "",
        description: role?.description || "",
        status: role?.status || "active",
      });
      setShowUnsaved(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, role]);

  const handleClose = () => {
    if (hasUnsaved) {
      setShowUnsaved(true);
    } else {
      onClose();
    }
  };

  const onSubmit = async () => {
    const values = usedForm.getValues();
    setSaving(true);
    try {
      const payload = {
        ...values,
        code: values.code || values.name.toLowerCase().replace(/\s+/g, "_"),
        permissions: role?.permissions || {},
      };
      if (role) {
        await api.patch(`/roles/${role.id}`, payload);
        toast.success("Role updated successfully");
      } else {
        await api.post("/roles", payload);
        toast.success("Role created successfully");
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={o => { if (!o) handleClose(); }}>
        <DialogContent className="p-0 gap-0 rounded-xl max-w-5xl w-[95vw] max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#009A44] to-[#007a35] rounded-t-xl px-5 py-3.5 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="bg-white/15 rounded-lg p-1.5 shrink-0">
                <ShieldCheck className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-white font-semibold text-sm leading-tight">
                  {isEdit ? "Edit Role" : "Create Role"}
                </h2>
                <p className="text-white/70 text-[11px] mt-0.5 truncate">
                  {isEdit ? "Edit role details" : "Create a new role"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {hasUnsaved && (
                <span className="flex items-center gap-1 text-amber-200 text-[10px] bg-white/10 px-2 py-1 rounded-md">
                  <AlertTriangle className="h-3 w-3" />
                  Unsaved
                </span>
              )}
              <button
                onClick={handleClose}
                className="bg-white/10 hover:bg-white/20 text-white rounded-lg p-1.5 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Body — scrolls, fills remaining space */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-5 space-y-5">
              {/* Role Information */}
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Shield className="h-3.5 w-3.5 text-[#009A44]" />
                    <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Role Information</h3>
                  </div>
                </div>
                <div className="p-4 space-y-3.5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">Role Name <span className="text-red-500">*</span></label>
                      <Input
                        value={formName || ""}
                        onChange={e => usedForm.setValue("name", e.target.value, { shouldDirty: true })}
                        placeholder="e.g. Loan Officer"
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">Role Code</label>
                      <Input
                        value={usedForm.watch("code") || ""}
                        onChange={e => usedForm.setValue("code", e.target.value)}
                        placeholder="Auto-generated if empty"
                        className="h-9 text-sm"
                      />
                      <p className="text-[10px] text-gray-400">Unique identifier</p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">Description</label>
                    <Textarea
                      value={usedForm.watch("description") || ""}
                      onChange={e => usedForm.setValue("description", e.target.value)}
                      placeholder="Describe the responsibilities of this role..."
                      className="min-h-[56px] text-sm resize-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">Status</label>
                      <Select
                        value={usedForm.watch("status") || "active"}
                        onValueChange={v => usedForm.setValue("status", v as any)}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer — natural bottom */}
          <div className="shrink-0 border-t border-gray-100 bg-white px-5 py-3.5 flex items-center justify-center gap-3 rounded-b-xl">
            <Button type="button" variant="outline" size="sm" onClick={handleClose} className="h-9 px-4 text-sm">
              Cancel
            </Button>
            <Button
              disabled={saving || !formName?.trim()}
              onClick={usedForm.handleSubmit(onSubmit)}
              size="sm"
              className="h-9 px-5 text-sm bg-[#009A44] hover:bg-[#007a35] text-white"
            >
              {saving && <LoaderCircle className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {isEdit ? (
                <><Save className="h-3.5 w-3.5 mr-1.5" /> Save Role</>
              ) : (
                <><Plus className="h-3.5 w-3.5 mr-1.5" /> Create Role</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unsaved changes dialog */}
      <AlertDialog open={showUnsaved} onOpenChange={setShowUnsaved}>
        <AlertDialogContent className="rounded-xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Unsaved Changes
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-500">
              You have unsaved changes. Are you sure you want to leave without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg text-sm">Stay Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setShowUnsaved(false); onClose(); }}
              className="bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm"
            >
              Leave Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── User Form Modal ──────────────────────────────────────────────────
function UserForm({ open, onClose, user, onSuccess }: { open: boolean; onClose: () => void; user?: any; onSuccess: () => void }) {
  const [saving, setSaving] = useState(false);
  const isEdit = !!user;

  const form = useForm<z.infer<typeof userSchema>>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      password: "",
      roleId: undefined,
      role: "Member",
      status: "active",
      requiresPasswordChange: true,
      memberId: null,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        first_name: user?.first_name || user?.firstName || "",
        last_name: user?.last_name || user?.lastName || "",
        email: user?.email || "",
        phone: user?.phone || "",
        password: "",
        roleId: user?.roleId || undefined,
        role: user?.role || "Member",
        status: user?.status || "active",
        requiresPasswordChange: user?.requiresPasswordChange !== false,
        memberId: user?.memberId ?? null,
      });
    }
  }, [open, user, form]);

  const { data: roles } = useQuery(["roles-for-dropdown"], async () => {
    const { data } = await api.get("/roles?page_size=100&status=active");
    return data.results || [];
  }, { enabled: open });

  const { data: members = [] } = useQuery(["members-for-user-form"], async () => {
    const { data } = await api.get("/members?limit=500&status=Active");
    return data.results || data.data || [];
  }, { enabled: open });

  const handleMemberSelect = (member: any) => {
    form.setValue("first_name", member.fullNames?.split(" ")[0] || "");
    form.setValue("last_name", member.fullNames?.split(" ").slice(1).join(" ") || "");
    form.setValue("email", member.email || "");
    form.setValue("phone", member.phone || member.telephone || "");
    form.setValue("memberId", member.id);
  };

  const onSubmit = async (values: z.infer<typeof userSchema>) => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/users/${user.id}`, values);
        toast.success("User updated successfully");
      } else {
        if (!values.password) {
          toast.error("Password is required for new users");
          setSaving(false);
          return;
        }
        await api.post("/users", values);
        toast.success("User created successfully");
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader className="border-b border-gray-100 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-[#009A44]/10 to-[#007a35]/10 border border-[#009A44]/20">
              <UserCog className="h-5 w-5 text-[#009A44]" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-gray-900">{isEdit ? "Edit User" : "Add New User"}</DialogTitle>
              <p className="text-sm text-gray-500 mt-0.5">{isEdit ? "Update user details and linked member" : "Create a new system user account"}</p>
            </div>
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="pt-2">
            <div className="space-y-5">
              {/* Member link section */}
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                <label className="flex items-center gap-2 text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-3">
                  <Users className="h-3.5 w-3.5" />
                  Link to Member Profile
                </label>
                <Select onValueChange={(v) => {
                  const member = members.find((m: any) => String(m.id) === v);
                  if (member) handleMemberSelect(member);
                }}>
                  <FormControl>
                    <SelectTrigger className="h-10 bg-white border-emerald-200 text-sm">
                      <SelectValue placeholder="Select a member to auto-fill..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="max-h-60">
                    {members.length === 0 ? (
                      <div className="px-2 py-6 text-center text-sm text-muted-foreground">No members found</div>
                    ) : members.map((m: any) => (
                      <SelectItem key={m.id} value={String(m.id)} className="text-sm">
                        <span className="font-medium">{m.fullNames || m.name}</span>
                        <span className="text-muted-foreground ml-2">· {m.email}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-emerald-600/70 mt-1.5">Selecting a member will auto-fill the fields below</p>
              </div>

              {/* Name row */}
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="first_name" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-gray-600">First Name *</FormLabel>
                    <FormControl>
                      <Input {...field} className="h-9 text-sm rounded-lg border-gray-300 focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="last_name" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-gray-600">Last Name *</FormLabel>
                    <FormControl>
                      <Input {...field} className="h-9 text-sm rounded-lg border-gray-300 focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              {/* Email & Phone row */}
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-gray-600">Email *</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} className="h-9 text-sm rounded-lg border-gray-300 focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="phone" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-gray-600">Phone</FormLabel>
                    <FormControl>
                      <Input {...field} className="h-9 text-sm rounded-lg border-gray-300 focus:border-[#009A44] focus:ring-1 focus:ring-[#009A44]/20" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              {/* Role & Status row */}
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="roleId" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-gray-600">Role</FormLabel>
                    <Select value={field.value ? String(field.value) : ""} onValueChange={v => field.onChange(v ? Number(v) : null)}>
                      <FormControl>
                        <SelectTrigger className="h-9 text-sm rounded-lg border-gray-300">
                          <SelectValue placeholder="Select role..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(roles || []).map((r: any) => (
                          <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="status" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-gray-600">Status</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="h-9 text-sm rounded-lg border-gray-300">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            Active
                          </span>
                        </SelectItem>
                        <SelectItem value="inactive">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-gray-400" />
                            Inactive
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              {/* Password (new users only) */}
              {!isEdit && (
                <FormField control={form.control} name="password" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-gray-600">Password *</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="Temporary password" {...field} className="h-9 text-sm rounded-lg border-gray-300" />
                    </FormControl>
                    <FormMessage />
                    <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-1">
                      <Info className="h-3 w-3" />
                      Enter a temporary password. User will be prompted to change it on first login.
                    </p>
                  </FormItem>
                )} />
              )}

              {/* Force password change toggle */}
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <Lock className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-xs font-semibold text-gray-700">Force password change</p>
                    <p className="text-[10px] text-gray-400">Require password change on first login</p>
                  </div>
                </div>
                <FormField control={form.control} name="requiresPasswordChange" render={({ field }) => (
                  <FormItem className="!mt-0">
                    <FormControl>
                      <Switch checked={field.value || false} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )} />
              </div>
            </div>

            <DialogFooter className="mt-6 pt-4 border-t border-gray-100 gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline" className="h-9 rounded-lg text-sm">Cancel</Button>
              </DialogClose>
              <Button type="submit" disabled={saving} className="h-9 rounded-lg text-sm bg-[#009A44] hover:bg-[#007a35] text-white">
                {saving && <LoaderCircle className="h-4 w-4 animate-spin mr-1.5" />}
                {isEdit ? "Update User" : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Create Role Page (full-page form) ─────────────────────────────
function CreateRolePage({ onBack, onSuccess }: { onBack: () => void; onSuccess: () => void }) {
  const [saving, setSaving] = useState(false);
  const [showUnsaved, setShowUnsaved] = useState(false);

  const usedForm = useForm<z.infer<typeof roleSchema>>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: "",
      code: "",
      description: "",
      status: "active",
    },
  });

  const formName = usedForm.watch("name");

  const hasUnsaved = usedForm.formState.isDirty;

  const handleBack = () => {
    if (hasUnsaved) {
      setShowUnsaved(true);
    } else {
      onBack();
    }
  };

  const onSubmit = async () => {
    const values = usedForm.getValues();
    setSaving(true);
    try {
      const payload = {
        ...values,
        code: values.code || values.name.toLowerCase().replace(/\s+/g, "_"),
      };
      await api.post("/roles", payload);
      toast.success("Role created successfully");
      onSuccess();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 animate-in fade-in slide-in-from-top-1 duration-200">
      {/* Back */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button onClick={handleBack} className="inline-flex items-center gap-1 text-gray-500 hover:text-[#009A44] transition-colors">
          <ChevronLeft className="h-4 w-4" />
          Roles
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-gray-800 font-medium">New Role</span>
      </div>

      {/* Header */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#009A44]/10">
                <ShieldCheck className="h-6 w-6 text-[#009A44]" />
              </div>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="text-lg font-bold text-gray-900">New Role</h1>
                </div>
                <p className="text-sm text-gray-500 mt-1">Create a new role - grant permissions later via the Permissions Matrix</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleBack} className="h-9 text-sm rounded-lg">
                <ChevronLeft className="h-3.5 w-3.5 mr-1.5" /> Back
              </Button>
              <Button
                disabled={saving || !formName?.trim()}
                onClick={usedForm.handleSubmit(onSubmit)}
                size="sm"
                className="h-9 px-5 text-sm bg-[#009A44] hover:bg-[#007a35] text-white"
              >
                {saving && <LoaderCircle className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Create Role
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Role Information */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Shield className="h-3.5 w-3.5 text-[#009A44]" />
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Role Information</h3>
          </div>
        </div>
        <div className="p-4 space-y-3.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-700">Role Name <span className="text-red-500">*</span></label>
              <Input
                value={usedForm.watch("name") || ""}
                onChange={e => usedForm.setValue("name", e.target.value, { shouldDirty: true })}
                placeholder="e.g. Loan Officer"
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-700">Role Code</label>
              <Input
                value={usedForm.watch("code") || ""}
                onChange={e => usedForm.setValue("code", e.target.value)}
                placeholder="Auto-generated if empty"
                className="h-9 text-sm"
              />
              <p className="text-[10px] text-gray-400">Unique identifier</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">Description</label>
            <Textarea
              value={usedForm.watch("description") || ""}
              onChange={e => usedForm.setValue("description", e.target.value)}
              placeholder="Describe the responsibilities of this role..."
              className="min-h-[56px] text-sm resize-none"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-700">Status</label>
              <Select
                value={usedForm.watch("status") || "active"}
                onValueChange={v => usedForm.setValue("status", v as any)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div className="sticky bottom-4 z-20 bg-white border border-gray-200 rounded-xl shadow-lg px-5 py-3.5 flex items-center justify-center gap-3">
        <Button type="button" variant="outline" size="sm" onClick={handleBack} className="h-9 px-4 text-sm">
          Cancel
        </Button>
        <Button
          disabled={saving || !formName?.trim()}
          onClick={usedForm.handleSubmit(onSubmit)}
          size="sm"
          className="h-9 px-5 text-sm bg-[#009A44] hover:bg-[#007a35] text-white"
        >
          {saving && <LoaderCircle className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Create Role
        </Button>
      </div>

      {/* Unsaved changes dialog */}
      <AlertDialog open={showUnsaved} onOpenChange={setShowUnsaved}>
        <AlertDialogContent className="rounded-xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Unsaved Changes
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-500">
              You have unsaved changes. Are you sure you want to leave without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg text-sm">Stay Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setShowUnsaved(false); onBack(); }}
              className="bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm"
            >
              Leave Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────
export default function UsersRoles() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("users");
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [auditSearch, setAuditSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [rolePage, setRolePage] = useState(1);
  const [auditPage, setAuditPage] = useState(1);
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [roleFormOpen, setRoleFormOpen] = useState(false);
  const [creatingRole, setCreatingRole] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [userStatusFilter, setUserStatusFilter] = useState<string[]>([]);
  const [roleStatusFilter, setRoleStatusFilter] = useState<string[]>([]);
  const [auditActionFilter, setAuditActionFilter] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);

  const confirmModal = useConfirmModal();

  // ── Queries ──
  const { data: usersData, isLoading: usersLoading, refetch: refetchUsers } = useQuery(
    ["system-users", userPage, userSearch, userStatusFilter],
    async () => {
      const params = new URLSearchParams();
      params.set("page", String(userPage));
      params.set("page_size", "15");
      if (userSearch) params.set("search", userSearch);
      if (userStatusFilter.length > 0) {
        params.set("filters", JSON.stringify([{ field: "status", value: userStatusFilter }]));
      }
      const { data } = await api.get(`/users?${params}`);
      return data;
    },
  );

  const { data: rolesData, isLoading: rolesLoading, refetch: refetchRoles } = useQuery(
    ["roles", rolePage, roleSearch, roleStatusFilter],
    async () => {
      const params = new URLSearchParams();
      params.set("page", String(rolePage));
      params.set("page_size", "25");
      if (roleSearch) params.set("search", roleSearch);
      if (roleStatusFilter.length > 0) {
        params.set("filters", JSON.stringify([{ field: "status", value: roleStatusFilter }]));
      }
      const { data } = await api.get(`/roles?${params}`);
      return data;
    },
  );

  const { data: auditData, isLoading: auditLoading, refetch: refetchAudit } = useQuery(
    ["audit-logs", auditPage, auditSearch, auditActionFilter],
    async () => {
      const params = new URLSearchParams();
      params.set("page", String(auditPage));
      params.set("page_size", "25");
      if (auditSearch) params.set("search", auditSearch);
      if (auditActionFilter) params.set("action", auditActionFilter);
      const { data } = await api.get(`/audit-logs?${params}`);
      return data;
    },
  );

  const users = usersData?.results || [];
  const usersTotal = usersData?.total || 0;
  const roles = rolesData?.results || [];
  const rolesTotal = rolesData?.total || 0;
  const auditLogs = auditData?.results || [];
  const auditTotal = auditData?.total || 0;

  // ── Role Mutations ──
  const toggleRoleStatus = async (role: any) => {
    setTogglingId(role.id);
    try {
      await api.patch(`/roles/${role.id}/toggle-status`);
      const newStatus = role.status === "active" ? "inactive" : "active";
      toast.success(`Role "${role.name}" ${newStatus === "active" ? "activated" : "deactivated"}`);
      refetchRoles();
      queryClient.invalidateQueries("roles");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to toggle status");
    } finally {
      setTogglingId(null);
    }
  };

  const duplicateRole = async (role: any) => {
    setDuplicatingId(role.id);
    try {
      await api.post(`/roles/${role.id}/duplicate`);
      toast.success(`Role "${role.name}" duplicated`);
      refetchRoles();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to duplicate role");
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDeleteRole = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/roles/${deleteTarget.id}`);
      toast.success(`Role "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      refetchRoles();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete role");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── User Mutations ──
  const toggleUserStatus = async (user: any) => {
    try {
      await api.patch(`/users/${user.id}/status`);
      toast.success(`User ${user.status === "active" ? "deactivated" : "activated"}`);
      refetchUsers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to toggle status");
    }
  };

  const resetUserPassword = async (user: any) => {
    confirmModal.open({
      title: "Reset Password",
      message: `Reset password for "${user.fullName || user.name}"? A temporary password will be generated.`,
      onConfirm: async () => {
        try {
          const { data } = await api.post(`/users/${user.id}/reset-password`);
          confirmModal.close();
          toast.success("Password reset successful", {
            description: `Temporary password: ${data.data?.temporaryPassword || "Check logs"}`,
            duration: 15000,
          });
        } catch (err: any) {
          toast.error(err?.response?.data?.message || "Failed to reset password");
        }
      },
    });
  };

  const deleteUser = async (user: any) => {
    confirmModal.open({
      title: "Delete User",
      message: `Are you sure you want to delete "${user.fullName || user.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await api.delete(`/users/${user.id}`);
          toast.success("User deleted");
          refetchUsers();
        } catch (err: any) {
          toast.error(err?.response?.data?.message || "Failed to delete user");
        }
      },
    });
  };

  const openEditUser = (user: any) => {
    setEditingUser(user);
    setUserFormOpen(true);
  };

  const openEditRole = (role: any) => {
    setEditingRole(role);
    setRoleFormOpen(true);
  };

  const onUserFormSuccess = () => {
    refetchUsers();
    queryClient.invalidateQueries("system-users");
  };

  const onRoleFormSuccess = () => {
    refetchRoles();
    queryClient.invalidateQueries("roles");
  };

  const activeUserCount = useMemo(() => users.filter((u: any) => u.status === "active").length, [users]);
  const inactiveUserCount = useMemo(() => users.filter((u: any) => u.status === "inactive").length, [users]);
  const activeRoleCount = useMemo(() => roles.filter((r: any) => r.status === "active").length, [roles]);

  const [userSorting, setUserSorting] = useState<SortingState>([]);
  const [roleSorting, setRoleSorting] = useState<SortingState>([]);

  const userColumns: ColumnDef<any>[] = useMemo(() => [
    {
      accessorKey: "fullName",
      header: ({ column }) => <DataTableColumnHeader column={column} title="User" />,
      cell: ({ row }) => (
        <div className="flex items-center gap-2.5">
          <Avatar className="h-8 w-8 rounded-full ring-1 ring-gray-100">
            <AvatarImage src={row.original.profileUrl} />
            <AvatarFallback className="text-[11px] bg-[#0A9F43]/10 text-[#0A9F43] font-semibold">
              {(row.original.fullName || row.original.name || row.original.email || "U").charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-slate-800 text-[14px]">{row.original.fullName || row.original.name}</p>
            <p className="text-[12px] text-slate-400">{row.original.email}</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "email",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Email / Phone" />,
      cell: ({ row }) => (
        <div>
          <div className="text-[13px]">{row.original.email}</div>
          {row.original.phone && <div className="text-[11px] text-slate-400 mt-0.5">{row.original.phone}</div>}
        </div>
      ),
    },
    {
      accessorKey: "roleName",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Role" />,
      cell: ({ row }) => (
        <Badge variant="outline" className="text-[11px] font-medium bg-blue-50/80 text-blue-700 border-blue-200/60 rounded-md px-2 py-0.5">
          {row.original.roleName || row.original.role}
        </Badge>
      ),
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => (
        <Badge className={`text-[11px] font-medium border-0 rounded-md px-2 py-0.5 ${row.original.status === "active" ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${row.original.status === "active" ? "bg-green-500" : "bg-slate-400"}`} />
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: "lastLoginAt",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Last Login" />,
      cell: ({ row }) => <span className="text-[13px] text-slate-500">{formatDateTime(row.original.lastLoginAt)}</span>,
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      cell: ({ row }) => <span className="text-[13px] text-slate-500">{formatDate(row.original.createdAt)}</span>,
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <DataTableRowActions
          row={row}
          actions={[
            { title: "Edit", onClick: (u: any) => openEditUser(u) },
            { title: row.original.status === "active" ? "Deactivate" : "Activate", onClick: (u: any) => toggleUserStatus(u) },
            { title: "Reset Password", onClick: (u: any) => resetUserPassword(u) },
            { title: "Delete", onClick: (u: any) => deleteUser(u) },
          ]}
        />
      ),
    },
  ], [openEditUser, toggleUserStatus, resetUserPassword, deleteUser]);

  const roleColumns: ColumnDef<any>[] = useMemo(() => [
    {
      accessorKey: "name",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Role Name" />,
      cell: ({ row }) => (
        <div>
          <button
            onClick={() => {
              setSelectedRoleId(row.original.id);
              setActiveTab("permissions");
            }}
            className="font-medium text-sm text-gray-800 hover:text-[#009A44] transition-colors text-left"
          >
            {row.original.name}
          </button>
          {row.original.code && <span className="text-[10px] text-gray-400 font-mono ml-2">{row.original.code}</span>}
        </div>
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Description" />,
      cell: ({ row }) => <span className="text-sm text-gray-500 truncate block">{row.original.description || "\u2014"}</span>,
    },
    {
      accessorKey: "usersAssigned",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Users" />,
      cell: ({ row }) => (
        <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold">
          {row.original.usersAssigned ?? 0}
        </span>
      ),
    },
    {
      accessorKey: "permissionsCount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Permissions" />,
      cell: ({ row }) => (
        <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-full bg-purple-50 text-purple-700 text-[11px] font-semibold">
          {row.original.permissionsCount ?? 0}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${
          row.original.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${row.original.status === "active" ? "bg-emerald-500" : "bg-gray-400"}`} />
          {row.original.status === "active" ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      cell: ({ row }) => <span className="text-xs text-gray-500">{formatDate(row.original.createdAt)}</span>,
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <DataTableRowActions
          row={row}
          actions={[
            { title: "Edit", onClick: (r: any) => openEditRole(r) },
            { title: "Duplicate", onClick: (r: any) => duplicateRole(r), disabled: duplicatingId === row.original.id },
            { title: row.original.status === "active" ? "Deactivate" : "Activate", onClick: (r: any) => toggleRoleStatus(r) },
            { title: "Delete", onClick: (r: any) => setDeleteTarget(r) },
          ]}
        />
      ),
    },
  ], [setActiveTab, openEditRole, duplicateRole, duplicatingId, toggleRoleStatus, setDeleteTarget]);

  if (creatingRole) {
    return (
      <div className="p-4 md:p-6">
        <CreateRolePage
          onBack={() => setCreatingRole(false)}
          onSuccess={() => { setCreatingRole(false); onRoleFormSuccess(); }}
        />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Hero Header Card */}
      <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden rounded-[14px]">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white flex items-center gap-2">
                <Shield className="h-6 w-6 text-white/90" />
                Users & Roles Management
              </h1>
              <p className="mt-1.5 text-sm sm:text-base text-white/80 max-w-xl leading-relaxed">
                Manage system users, roles, permissions, and view audit logs
              </p>
            </div>
            <div className="flex items-center gap-2 mt-2 sm:mt-0">
              <Button
                size="sm"
                onClick={() => { setEditingUser(null); setUserFormOpen(true); }}
                className="bg-white text-[#009A44] hover:bg-white/90 font-semibold h-9 rounded-lg"
              >
                <UserPlus className="h-4 w-4 mr-1.5" /> Add User
              </Button>
              <Button
                size="sm"
                onClick={() => { setEditingRole(null); setCreatingRole(true); }}
                className="bg-white/15 text-white hover:bg-white/25 border border-white/20 h-9 rounded-lg"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add Role
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-[14px]">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-gray-500">Total Users</p>
                <p className="mt-1 text-lg font-bold tracking-tight text-[#0b0e2a]">
                  {usersLoading ? <LoaderCircle className="h-4 w-4 animate-spin inline" /> : usersTotal.toLocaleString()}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">registered accounts</p>
              </div>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0A9F43]/10">
                <Users className="h-4 w-4 text-[#0A9F43]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-[14px]">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-gray-500">Active Users</p>
                <p className="mt-1 text-lg font-bold tracking-tight text-[#0b0e2a]">
                  {usersLoading ? <LoaderCircle className="h-4 w-4 animate-spin inline" /> : activeUserCount.toLocaleString()}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">{inactiveUserCount} inactive</p>
              </div>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                <UserCheck className="h-4 w-4 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-[14px]">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-gray-500">Total Roles</p>
                <p className="mt-1 text-lg font-bold tracking-tight text-[#0b0e2a]">
                  {rolesLoading ? <LoaderCircle className="h-4 w-4 animate-spin inline" /> : rolesTotal.toLocaleString()}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">{activeRoleCount} active</p>
              </div>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-50">
                <Shield className="h-4 w-4 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-[14px]">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-gray-500">Permission Groups</p>
                <p className="mt-1 text-lg font-bold tracking-tight text-[#0b0e2a]">{MODULES.length}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">system modules</p>
              </div>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50">
                <KeyRound className="h-4 w-4 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-[10px] gap-1">
          <TabsTrigger
            value="users"
            className="gap-2 rounded-[8px] px-3 py-1.5 text-xs font-medium data-[state=active]:bg-white data-[state=active]:text-[#009A44] data-[state=active]:shadow-sm data-[state=active]:font-semibold transition-all"
          >
            <Users className="h-3.5 w-3.5" /> Users
          </TabsTrigger>
          <TabsTrigger
            value="roles"
            className="gap-2 rounded-[8px] px-3 py-1.5 text-xs font-medium data-[state=active]:bg-white data-[state=active]:text-[#009A44] data-[state=active]:shadow-sm data-[state=active]:font-semibold transition-all"
          >
            <Shield className="h-3.5 w-3.5" /> Roles
          </TabsTrigger>
          <TabsTrigger
            value="permissions"
            className="gap-2 rounded-[8px] px-3 py-1.5 text-xs font-medium data-[state=active]:bg-white data-[state=active]:text-[#009A44] data-[state=active]:shadow-sm data-[state=active]:font-semibold transition-all"
          >
            <KeyRound className="h-3.5 w-3.5" /> Permissions Matrix
          </TabsTrigger>
          <TabsTrigger
            value="audit"
            className="gap-2 rounded-[8px] px-3 py-1.5 text-xs font-medium data-[state=active]:bg-white data-[state=active]:text-[#009A44] data-[state=active]:shadow-sm data-[state=active]:font-semibold transition-all"
          >
            <ScrollText className="h-3.5 w-3.5" /> Audit Logs
          </TabsTrigger>
        </TabsList>

        {/* ───────────── USERS TAB ───────────── */}
        <TabsContent value="users" className="space-y-4 mt-4">
          <DataTable
            columns={userColumns}
            data={users}
            isLoading={usersLoading}
            onSearch={(value) => { setUserSearch(value); setUserPage(1); }}
            setSorting={setUserSorting}
            sorting={userSorting}
            setPagination={(updater) => {
              if (typeof updater === "function") {
                const newState = updater({ pageIndex: userPage - 1, pageSize: 15 });
                setUserPage(newState.pageIndex + 1);
              }
            }}
            pageIndex={userPage - 1}
            pageSize={15}
            pageCount={Math.ceil(usersTotal / 15) || 1}
            Action={() => (
              <Button size="sm" onClick={() => { setEditingUser(null); setUserFormOpen(true); }} className="h-8 bg-[#009A44] hover:bg-[#007a35] text-white text-xs rounded-lg">
                <UserPlus className="h-3.5 w-3.5 mr-1" /> Add User
              </Button>
            )}
            emptyMessage="No users found"
            emptyDescription="Try adjusting your search or add a new user."
            showSearch
            enableExport={false}
          />
        </TabsContent>

        {/* ───────────── ROLES TAB ───────────── */}
        <TabsContent value="roles" className="space-y-4 mt-4">
          <DataTable
            columns={roleColumns}
            data={roles}
            isLoading={rolesLoading}
            onSearch={(value) => { setRoleSearch(value); setRolePage(1); }}
            setSorting={setRoleSorting}
            sorting={roleSorting}
            setPagination={(updater) => {
              if (typeof updater === "function") {
                const newState = updater({ pageIndex: rolePage - 1, pageSize: 25 });
                setRolePage(newState.pageIndex + 1);
              }
            }}
            pageIndex={rolePage - 1}
            pageSize={25}
            pageCount={Math.ceil(rolesTotal / 25) || 1}
            Action={() => (
              <Button size="sm" onClick={() => { setEditingRole(null); setCreatingRole(true); }} className="h-8 bg-[#009A44] hover:bg-[#007a35] text-white text-xs rounded-lg">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Role
              </Button>
            )}
            emptyMessage="No roles found"
            emptyDescription="Try adjusting your search or create a new role."
            showSearch
            enableExport={false}
          />
        </TabsContent>

        {/* ───────────── PERMISSIONS MATRIX TAB ───────────── */}
        <TabsContent value="permissions" className="space-y-4 mt-4">
          <Card className="border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-[14px]">
            <div className="px-4 sm:px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-slate-800">Permissions Matrix</h3>
              <p className="text-[11px] text-slate-400 mt-0.5">Compact matrix with multi-select (Ctrl+Click, Shift+Click), bulk grant/revoke, column/row operations, and Grant All / Revoke All.</p>
            </div>
            <div className="p-4 sm:p-5">
              <PermissionsMatrix roles={roles} initialRoleId={selectedRoleId ?? undefined} />
            </div>
          </Card>
        </TabsContent>

        {/* ───────────── AUDIT LOGS TAB ───────────── */}
        <TabsContent value="audit" className="space-y-4 mt-4">
          <Card className="border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-[14px] overflow-hidden">
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Audit Logs</h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Track all user and role changes across the system</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    placeholder="Search logs..."
                    value={auditSearch}
                    onChange={e => { setAuditSearch(e.target.value); setAuditPage(1); }}
                    className="pl-7 w-48 h-8 text-xs rounded-lg"
                  />
                </div>
                <Select value={auditActionFilter} onValueChange={v => { setAuditActionFilter(v); setAuditPage(1); }}>
                  <SelectTrigger className="w-32 h-8 text-xs rounded-lg">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="user.created">User Created</SelectItem>
                    <SelectItem value="user.updated">User Updated</SelectItem>
                    <SelectItem value="user.deleted">User Deleted</SelectItem>
                    <SelectItem value="user.activated">User Activated</SelectItem>
                    <SelectItem value="user.deactivated">User Deactivated</SelectItem>
                    <SelectItem value="user.password_reset">Password Reset</SelectItem>
                    <SelectItem value="user.role_assigned">Role Assigned</SelectItem>
                    <SelectItem value="role.created">Role Created</SelectItem>
                    <SelectItem value="role.updated">Role Updated</SelectItem>
                    <SelectItem value="role.deleted">Role Deleted</SelectItem>
                    <SelectItem value="role.activated">Role Activated</SelectItem>
                    <SelectItem value="role.deactivated">Role Deactivated</SelectItem>
                    <SelectItem value="role.duplicated">Role Duplicated</SelectItem>
                    <SelectItem value="role.permissions_updated">Permissions Updated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0A9F43]/5 border-y border-gray-100">
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 text-[13px]">Date</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 text-[13px]">Actor</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 text-[13px]">Action</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 text-[13px]">Target</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 text-[13px]">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLoading ? (
                    <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400"><LoaderCircle className="h-6 w-6 animate-spin mx-auto" /></td></tr>
                  ) : auditLogs.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        <ScrollText className="h-10 w-10 text-slate-300" />
                        <p className="text-sm text-slate-500">No audit logs found</p>
                      </div>
                    </td></tr>
                  ) : auditLogs.map((log: any) => {
                      const actionLabels: Record<string, string> = {
                        "user.created": "User Created",
                        "user.updated": "User Updated",
                        "user.deleted": "User Deleted",
                        "user.activated": "User Activated",
                        "user.deactivated": "User Deactivated",
                        "user.password_reset": "Password Reset",
                        "user.role_assigned": "Role Assigned",
                        "role.created": "Role Created",
                        "role.updated": "Role Updated",
                        "role.deleted": "Role Deleted",
                        "role.activated": "Role Activated",
                        "role.deactivated": "Role Deactivated",
                        "role.duplicated": "Role Duplicated",
                        "role.permissions_updated": "Permissions Updated",
                      };
                      const actionColors: Record<string, string> = {
                        "user.created": "bg-green-100 text-green-700",
                        "user.activated": "bg-green-100 text-green-700",
                        "role.created": "bg-green-100 text-green-700",
                        "role.activated": "bg-green-100 text-green-700",
                        "role.duplicated": "bg-blue-100 text-blue-700",
                        "user.deleted": "bg-red-100 text-red-700",
                        "role.deleted": "bg-red-100 text-red-700",
                        "user.deactivated": "bg-red-100 text-red-700",
                        "role.deactivated": "bg-red-100 text-red-700",
                        "user.password_reset": "bg-amber-100 text-amber-700",
                      };
                      const colorClass = actionColors[log.action] || "bg-slate-100 text-slate-600";
                      return (
                        <tr key={log.id} className="border-b border-gray-50 hover:bg-[#0A9F43]/[0.03] transition-colors">
                          <td className="px-4 py-3.5 text-slate-500 whitespace-nowrap text-[13px]">{formatDateTime(log.createdAt)}</td>
                          <td className="px-4 py-3.5 font-medium text-slate-700 text-[13px]">{log.actorName}</td>
                          <td className="px-4 py-3.5">
                            <Badge className={`text-[11px] font-medium border-0 rounded-md px-2 py-0.5 ${colorClass}`}>
                              {actionLabels[log.action] || log.action}
                            </Badge>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-slate-600 text-[13px]">{log.targetName || `#${log.targetId}`}</span>
                            <span className="text-[11px] text-slate-400 ml-1">({log.targetType})</span>
                          </td>
                          <td className="px-4 py-3.5 text-slate-500 max-w-[300px] truncate text-[13px]">{log.description || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {auditTotal > 25 && (
                <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-t border-gray-100 bg-slate-50/50">
                  <p className="text-xs text-slate-400">
                    Showing <span className="font-medium text-slate-600">{(auditPage - 1) * 25 + 1}</span> to <span className="font-medium text-slate-600">{Math.min(auditPage * 25, auditTotal)}</span> of <span className="font-medium text-slate-600">{auditTotal}</span> logs
                  </p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={auditPage <= 1} onClick={() => setAuditPage(p => p - 1)} className="text-xs h-8 rounded-lg px-3">Previous</Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(Math.ceil(auditTotal / 25), 5) }, (_, i) => {
                        const startPage = Math.max(1, Math.min(auditPage - 2, Math.ceil(auditTotal / 25) - 4));
                        const pageNum = startPage + i;
                        if (pageNum > Math.ceil(auditTotal / 25)) return null;
                        return (
                          <Button
                            key={pageNum}
                            size="sm"
                            variant={pageNum === auditPage ? "default" : "outline"}
                            onClick={() => setAuditPage(pageNum)}
                            className={`text-xs h-8 w-8 p-0 rounded-lg ${pageNum === auditPage ? "bg-[#009A44] hover:bg-[#00803a] text-white" : ""}`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button size="sm" variant="outline" disabled={auditPage >= Math.ceil(auditTotal / 25)} onClick={() => setAuditPage(p => p + 1)} className="text-xs h-8 rounded-lg px-3">Next</Button>
                  </div>
                </div>
              )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <UserForm
        open={userFormOpen}
        onClose={() => { setUserFormOpen(false); setEditingUser(null); }}
        user={editingUser}
        onSuccess={onUserFormSuccess}
      />
      <RoleForm
        open={roleFormOpen}
        onClose={() => { setRoleFormOpen(false); setEditingRole(null); }}
        role={editingRole}
        onSuccess={onRoleFormSuccess}
      />
      <ConfirmModal modal={confirmModal} />
    </div>
  );
}
